/**
 * Formatters for Ory message context values (dates, URLs, etc.).
 * 
 * Applications extend `AbstractOryMessageContextFormatter` and provide handlers
 * for formatting context values in messages.
 */
import type { OryMessageContextFor, OryMessageKeyWithContext } from './message-context';

/** Handler function for formatting a message's context values. */
export type OryMessageContextHandler<MessageKey extends OryMessageKeyWithContext> = (
  context: OryMessageContextFor<MessageKey>,
  locale: string
) => OryMessageContextFor<MessageKey>;

/** Type-safe handler map ensuring all message keys with context are handled. */
export type OryMessageContextHandlerMap = {
  [Key in OryMessageKeyWithContext]: OryMessageContextHandler<Key>
};

/**
 * Abstract formatter for Ory message context values.
 * 
 * Applications extend this class and provide handlers for formatting context values
 * (dates, URLs, numbers) for display in translations.
 */
export abstract class AbstractOryMessageContextFormatter {
  
  protected abstract readonly handlers: Partial<OryMessageContextHandlerMap>;

  format<MessageKey extends OryMessageKeyWithContext>(
    messageKey: MessageKey,
    context: OryMessageContextFor<MessageKey>,
    locale: string
  ): OryMessageContextFor<MessageKey> {
    const handler = this.handlers[messageKey];
    if (!handler) {
      return context;
    }
    return handler(context, locale);
  }
}

